import React, { useEffect, useState } from "react";
import SEO from "@/components/SEO";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { breadcrumbSchema } from "@/lib/organization-schema";
import { HelpCircle } from "lucide-react";
import prerenderedFaqs from "@/data/faqs-prerender.json";

export interface Faq {
  id: string;
  question: string;
  answer_intro: string | null;
  answer_bullets: string[];
  category: string;
  sort_order: number;
  published: boolean;
}

// Seed data baked in at build time so AI crawlers and search engines see the
// full FAQ content in the static HTML snapshot (react-snap can't await
// client-side Supabase fetches). Live data refreshes after hydration.
const SEED_FAQS = prerenderedFaqs as Faq[];

// Strip markdown [label](url) → label for plain-text contexts (JSON-LD).
function stripMarkdownLinks(s: string): string {
  return s.replace(/\[([^\]\n]+)\]\(([^)\s]+)\)/g, "$1");
}

function answerToPlainText(faq: Pick<Faq, "answer_intro" | "answer_bullets">) {
  const parts: string[] = [];
  if (faq.answer_intro) parts.push(stripMarkdownLinks(faq.answer_intro));
  if (faq.answer_bullets?.length)
    parts.push(faq.answer_bullets.map((b) => `• ${stripMarkdownLinks(b)}`).join("\n"));
  return parts.join("\n\n");
}

// Internal page mentions → route links. Order matters: match longer phrases first.
const INTERNAL_LINKS: Array<{ pattern: RegExp; to: string; label?: string }> = [
  { pattern: /\bContact page\b/g, to: "/contact" },
  { pattern: /\bPartner page\b/g, to: "/partner" },
  { pattern: /\bPrograms page\b/g, to: "/programs" },
  { pattern: /\bAbout page\b/g, to: "/about" },
  { pattern: /\bTransparency page\b/g, to: "/transparency" },
  { pattern: /\bFAQ page\b/g, to: "/faq" },
];

// Linkify a string. Recognizes (in priority order):
//  1. Markdown links [label](url) — author-controlled via Mission Control editor
//  2. Bare http(s) URLs
//  3. Bare email addresses
//  4. Bare give.abbashouse.org mention
//  5. Known internal page phrases ("Contact page", "Partner page", …)
function linkify(text: string): React.ReactNode[] {
  const internalSrc = INTERNAL_LINKS.map((l) => l.pattern.source).join("|");
  // Markdown link must come first so its inner URL isn't matched twice.
  const combined = new RegExp(
    `(\\[[^\\]\\n]+\\]\\([^)\\s]+\\)|https?:\\/\\/[^\\s)]+|[\\w.+-]+@[\\w-]+\\.[\\w.-]+|give\\.abbashouse\\.org|${internalSrc})`,
    "g",
  );
  const out: React.ReactNode[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  let key = 0;
  const cls = "underline font-medium text-foreground hover:text-primary transition-colors";

  while ((m = combined.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const token = m[0];

    // 1. Markdown [label](url)
    const md = /^\[([^\]\n]+)\]\(([^)\s]+)\)$/.exec(token);
    if (md) {
      const label = md[1];
      const href = md[2];
      if (href.startsWith("/")) {
        out.push(
          <Link key={`l-${key++}`} to={href} className={cls}>
            {label}
          </Link>,
        );
      } else if (href.startsWith("mailto:") || href.startsWith("tel:")) {
        out.push(
          <a key={`l-${key++}`} href={href} className={cls}>
            {label}
          </a>,
        );
      } else {
        const finalHref = /^https?:\/\//.test(href) ? href : `https://${href}`;
        out.push(
          <a
            key={`l-${key++}`}
            href={finalHref}
            className={cls}
            target="_blank"
            rel="noopener noreferrer"
          >
            {label}
          </a>,
        );
      }
    } else if (/^https?:\/\//.test(token)) {
      const clean = token.replace(/[.,;:)\]]+$/, "");
      const trail = token.slice(clean.length);
      out.push(
        <a key={`l-${key++}`} href={clean} className={cls} target="_blank" rel="noopener noreferrer">
          {clean.replace(/^https?:\/\//, "")}
        </a>,
      );
      if (trail) out.push(trail);
    } else if (/^[\w.+-]+@[\w-]+\.[\w.-]+$/.test(token)) {
      out.push(
        <a key={`l-${key++}`} href={`mailto:${token}`} className={cls}>
          {token}
        </a>,
      );
    } else if (/^give\.abbashouse\.org$/.test(token)) {
      out.push(
        <a key={`l-${key++}`} href="https://give.abbashouse.org" className={cls} target="_blank" rel="noopener noreferrer">
          give.abbashouse.org
        </a>,
      );
    } else {
      const match = INTERNAL_LINKS.find((l) => new RegExp(`^${l.pattern.source}$`).test(token));
      if (match) {
        out.push(
          <Link key={`l-${key++}`} to={match.to} className={cls}>
            {token}
          </Link>,
        );
      } else {
        out.push(token);
      }
    }
    last = m.index + token.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

export default function FAQ() {
  // Initialize with prerendered seed so first paint (and the react-snap
  // snapshot) contains real FAQ content for crawlers.
  const [faqs, setFaqs] = useState<Faq[]>(SEED_FAQS);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("faqs" as any)
        .select("*")
        .eq("published", true)
        .order("category", { ascending: true })
        .order("sort_order", { ascending: true });
      if (cancelled) return;
      if (data && (data as any[]).length) {
        setFaqs(data as unknown as Faq[]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Group by category, preserving order
  const grouped = faqs.reduce<Record<string, Faq[]>>((acc, f) => {
    (acc[f.category] ||= []).push(f);
    return acc;
  }, {});
  const categories = Object.keys(grouped);

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: answerToPlainText(f),
      },
    })),
  };

  const breadcrumb = breadcrumbSchema([
    { name: "Home", url: "https://abbashouse.org/" },
    { name: "FAQ", url: "https://abbashouse.org/faq" },
  ]);

  return (
    <main className="min-h-screen bg-background">
      <SEO
        title="FAQ — Frequently Asked Questions"
        description="Answers about Abba's House Communities — eligibility, costs, locations, how to apply, donating, and more."
        canonical="https://abbashouse.org/faq"
        jsonLd={faqs.length ? [faqSchema, breadcrumb] : [breadcrumb]}
      />

      {/* Hero */}
      <section className="bg-primary text-primary-foreground">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl py-16 md:py-24 text-center">
          <div className="inline-flex items-center gap-2 text-xs font-semibold tracking-widest uppercase opacity-80 mb-4">
            <HelpCircle className="h-4 w-4" /> Frequently Asked Questions
          </div>
          <h1 className="text-4xl md:text-5xl font-black tracking-tight">Answers, straight up.</h1>
          <p className="mt-4 text-base md:text-lg opacity-80 max-w-2xl mx-auto leading-relaxed">
            Everything you need to know about our programs, eligibility, costs, locations, and how to get involved.
          </p>
        </div>
      </section>

      {/* FAQ list */}
      <section className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl py-16 md:py-20">
        {faqs.length === 0 ? (
          <p className="text-center text-muted-foreground">
            We're preparing answers to common questions. In the meantime, reach out on our{" "}
            <Link to="/contact" className="underline font-medium text-foreground">Contact page</Link>.
          </p>
        ) : (
          <div className="space-y-14">
            {categories.map((category) => (
              <div key={category}>
                <h2 className="sr-only">{category}</h2>
                <p className="text-xs font-semibold tracking-widest uppercase text-muted-foreground mb-6">
                  {category}
                </p>
                <div className="space-y-10">
                  {grouped[category].map((faq) => (
                    <article
                      key={faq.id}
                      className="border-b border-border pb-10 last:border-b-0"
                      itemScope
                      itemProp="mainEntity"
                      itemType="https://schema.org/Question"
                    >
                      {/* Literal-question H2 — primary signal for AI bot answer-lifting */}
                      <h2
                        className="text-2xl md:text-3xl font-bold tracking-tight text-foreground"
                        itemProp="name"
                      >
                        {faq.question}
                      </h2>
                      <div
                        className="mt-4 space-y-4 text-base md:text-lg text-muted-foreground leading-relaxed"
                        itemScope
                        itemProp="acceptedAnswer"
                        itemType="https://schema.org/Answer"
                      >
                        <div itemProp="text">
                          {faq.answer_intro && <p>{linkify(faq.answer_intro)}</p>}
                          {faq.answer_bullets.length > 0 && (
                            <ul className="list-disc pl-6 space-y-2 marker:text-primary">
                              {faq.answer_bullets.map((bullet, i) => (
                                <li key={i}>{linkify(bullet)}</li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* CTA */}
        <div className="mt-20 text-center bg-secondary rounded-2xl p-10">
          <h2 className="text-2xl font-bold tracking-tight">Still have a question?</h2>
          <p className="mt-2 text-muted-foreground">
            Our team is happy to help. Reach out and we'll respond within two business days.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center justify-center mt-6 h-11 px-8 rounded-md bg-primary text-primary-foreground font-semibold hover:opacity-90 transition-opacity"
          >
            Contact Us
          </Link>
        </div>
      </section>
    </main>
  );
}
